import { Component, OnInit, HostListener, ElementRef, TemplateRef, ViewChild, AfterViewInit, ChangeDetectorRef, Input, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { RouterModule, Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { HeaderComponent } from '../header/header.component';
import { PageHeaderComponent } from '../page-header/page-header.component';
import { StepperComponent, StepperStep } from '../primeng';
import { CatalogueApiService, SearchCriteria } from '@angular-workspace/services';
import { Subject, debounceTime, distinctUntilChanged, switchMap, takeUntil, filter, from } from 'rxjs';

export interface ContributionFormData {
  title: string;
  permission: 'Open' | 'Restricted';
  metadataOption: 'enter' | 'upload' | null;
}

export interface ContributionSetupConfig {
  type: 'dataset' | 'model';
  routeBase: string; // e.g., 'provider/add-contributions/dataset'
  setupTitle: string; // e.g., 'Setup your datasets'
  titlePlaceholder: string; // e.g., 'Enter dataset title'
  localStorageKey: string; // e.g., 'datasetFormData'
  stepperSteps: StepperStep[];
  routes: string[]; // Array of routes for each step
}

@Component({
  selector: 'app-contribution-setup',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    HeaderComponent,
    PageHeaderComponent,
    StepperComponent,
  ],
  templateUrl: './contribution-setup.component.html',
  styleUrl: './contribution-setup.component.scss',
})
export class ContributionSetupComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() config!: ContributionSetupConfig;
  
  isPermissionDropdownOpen = false;
  isUpdateMode = false;

  currentStep = 0; // Step 1 (0-based index)
  totalSteps = 4;
  
  @ViewChild('step1Template', { static: true }) step1Template!: TemplateRef<any>;

  stepperSteps: StepperStep[] = [];
  contributionForm!: FormGroup;

  /** UI labels: "Public" / "Restricted". Form value stays "Open" | "Restricted" for API (OPEN/RESTRICTED). */
  permissionOptions: Array<{ value: 'Open' | 'Restricted'; label: string }> = [
    { value: 'Open', label: 'Public' },
    { value: 'Restricted', label: 'Restricted' }
  ];

  // Title availability check
  private catalogueApiService = inject(CatalogueApiService);
  private destroy$ = new Subject<void>();
  titleAvailabilityStatus: 'checking' | 'available' | 'not-available' | null = null;
  isCheckingTitle = false;
  
  // Track if user is within the contribution flow
  private readonly FLOW_FLAG_KEY = 'contributionFlowActive';
  private previousRoute: string = '';

  constructor(
    private fb: FormBuilder,
    private elementRef: ElementRef,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef
  ) {}
  
  ngOnInit(): void {
    // Initialize stepper steps from config
    this.stepperSteps = this.config.stepperSteps || [];
    
    // Get previous route from browser history or referrer
    const referrer = document.referrer;
    const currentRoute = this.router.url;
    const isBaseRoute = currentRoute === `/${this.config.routes[0]}` || 
                       currentRoute.endsWith(`/${this.config.routes[0]}`) ||
                       currentRoute === this.config.routes[0];
    
    let initialTitle = '';
    let initialPermission = '';
    
    // Check if user is coming from within the flow or from outside
    const isInFlow = this.isRouteInFlow(referrer);
    const hasFlowFlag = sessionStorage.getItem(this.FLOW_FLAG_KEY) === 'true';
    const navigation = this.router.getCurrentNavigation();
    const navigationState = navigation?.extras?.state;
    const historyState = window.history.state;
    const hasStepperState = (navigationState && navigationState['contributionData']) || 
                           (historyState && historyState['contributionData']);
    
    // If user navigated directly to step 1 (base route)
    if (isBaseRoute) {
      // If coming from within flow (stepper/previous) or has navigation state, restore data
      if (hasStepperState || (hasFlowFlag && isInFlow) || hasFlowFlag) {
        // User came from stepper/previous - restore from navigation state or localStorage
        const contributionData = navigationState?.['contributionData'] || historyState?.['contributionData'];
        if (contributionData) {
          initialTitle = contributionData.title || '';
          initialPermission = contributionData.permission || '';
        } else {
          // Always restore from localStorage when flow flag is set (for Previous button navigation)
          const savedData = localStorage.getItem(this.config.localStorageKey);
          if (savedData) {
            try {
              const parsedData = JSON.parse(savedData);
              initialTitle = parsedData.title || '';
              initialPermission = parsedData.permission || '';
            } catch (error) {
              console.error(`Error parsing saved ${this.config.type} data:`, error);
              localStorage.removeItem(this.config.localStorageKey);
            }
          }
        }
        // Keep flow flag active
        sessionStorage.setItem(this.FLOW_FLAG_KEY, 'true');
      } else {
        // User navigated directly from outside (e.g., My Projects) - clear saved data and start fresh
        localStorage.removeItem(this.config.localStorageKey);
        sessionStorage.removeItem(this.FLOW_FLAG_KEY);
        initialTitle = '';
        initialPermission = '';
      }
    } else {
      // User is on a different step - restore from localStorage if available
      const savedData = localStorage.getItem(this.config.localStorageKey);
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          initialTitle = parsedData.title || '';
          initialPermission = parsedData.permission || '';
        } catch (error) {
          console.error(`Error parsing saved ${this.config.type} data:`, error);
          localStorage.removeItem(this.config.localStorageKey);
        }
      }
      // Mark that user is in the flow
      sessionStorage.setItem(this.FLOW_FLAG_KEY, 'true');
    }
    
    // Initialize form
    this.contributionForm = this.fb.group({
      title: [initialTitle, [Validators.required]],
      permission: [initialPermission, [Validators.required]],
      metadataOption: [null]
    });

    // When navigating back to Step 1 within an active create flow, the upload
    // step may have written itemId/id into localStorage after creating the asset.
    // Strip those IDs so applyUpdateModeLock() does not incorrectly freeze the
    // form – the user should still be able to edit and re-submit.
    if (isBaseRoute && hasFlowFlag) {
      const rawForStrip = localStorage.getItem(this.config.localStorageKey);
      if (rawForStrip) {
        try {
          const parsedForStrip = JSON.parse(rawForStrip);
          if (parsedForStrip?.itemId || parsedForStrip?.id || parsedForStrip?.item_id) {
            delete parsedForStrip.itemId;
            delete parsedForStrip.id;
            delete parsedForStrip.item_id;
            localStorage.setItem(this.config.localStorageKey, JSON.stringify(parsedForStrip));
          }
        } catch { /* ignore malformed data */ }
      }
    }

    // If itemId exists in localStorage, this is an update flow: lock step 1 fields
    this.applyUpdateModeLock();
    
    // Mark form controls as touched if they have values (for proper validation state)
    if (initialTitle) {
      this.contributionForm.get('title')?.markAsTouched();
    }
    if (initialPermission) {
      this.contributionForm.get('permission')?.markAsTouched();
    }
    
    // Auto-save form data on changes (debounced)
    this.contributionForm.valueChanges
      .pipe(
        debounceTime(300),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.saveFormData();
      });

    // Subscribe to title changes for availability check (for both datasets and models)
    if (this.config.type === 'dataset' || this.config.type === 'model') {
      this.contributionForm.get('title')?.valueChanges
        .pipe(
          debounceTime(500), // Wait 500ms after user stops typing
          distinctUntilChanged(), // Only check if value actually changed
          switchMap(title => {
            this.titleAvailabilityStatus = 'checking';
            this.isCheckingTitle = true;
            this.cdr.detectChanges();
            // Convert Promise to Observable for switchMap
            return from(this.checkTitleAvailability(title));
          }),
          takeUntil(this.destroy$)
        )
        .subscribe({
          next: (isAvailable) => {
            this.isCheckingTitle = false;
            this.titleAvailabilityStatus = isAvailable ? 'available' : 'not-available';
            this.cdr.detectChanges();
          },
          error: (error) => {
            console.error('Error checking title availability:', error);
            this.isCheckingTitle = false;
            this.titleAvailabilityStatus = null;
            this.cdr.detectChanges();
          }
        });
      
      // If title was restored, trigger availability check immediately
      if (initialTitle && initialTitle.trim().length > 0) {
        setTimeout(() => {
          this.checkTitleAvailability(initialTitle).then((isAvailable) => {
            this.titleAvailabilityStatus = isAvailable ? 'available' : 'not-available';
            this.isCheckingTitle = false;
            this.cdr.detectChanges();
          }).catch((error) => {
            console.error('Error checking title availability on init:', error);
            this.isCheckingTitle = false;
            this.titleAvailabilityStatus = null;
            this.cdr.detectChanges();
          });
        }, 100);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async checkTitleAvailability(title: string): Promise<boolean> {
    if (!title || title.trim().length === 0) {
      this.titleAvailabilityStatus = null;
      return true; // Empty title is considered available
    }

    try {
      // Determine asset type based on config
      const assetTypes = this.config.type === 'model' ? ['Model'] : ['Dataset'];
      
      // Search for assets (datasets or models) with the exact name using searchCriteria
      const searchCriteria: SearchCriteria[] = [
        {
          searchType: 'term',
          field: 'name',
          values: [title.trim()]
        }
      ];

      const response = await this.catalogueApiService.searchAllAssets(
        '', // No search query - using searchCriteria instead
        assetTypes, // assetTypes - will be mapped to adex:DataBank or adex:AiModel
        [], // customFilter
        1, // page
        10, // size
        'name:asc;itemCreatedAt:asc;', // sort
        searchCriteria // Search by label field
      );

      // Extract items from response
      let items: any[] = [];
      if (Array.isArray(response)) {
        items = response;
      } else if (response?.result && Array.isArray(response.result)) {
        items = response.result;
      } else if (response?.data && Array.isArray(response.data)) {
        items = response.data;
      } else if (response?.items && Array.isArray(response.items)) {
        items = response.items;
      }

      // Check if any item has a matching name (case-insensitive)
      const titleLower = title.trim().toLowerCase();
      const hasMatch = items.some(item => {
        const itemName = (item.name || item.label || '').toLowerCase();
        return itemName === titleLower;
      });

      // Return true if no match found (title is available), false if match found (title is not available)
      return !hasMatch;
    } catch (error) {
      console.error('Error checking title availability:', error);
      // On error, assume title is available to not block user
      return true;
    }
  }

  ngAfterViewInit(): void {
    // Update step 1 with template reference after view is initialized
    if (this.step1Template) {
      this.stepperSteps[0].content = this.step1Template;
      this.cdr.detectChanges();
    }
    
    // Restore form data from localStorage if available and flow flag is set
    // This ensures data is restored when navigating back via Previous button
    const hasFlowFlag = sessionStorage.getItem(this.FLOW_FLAG_KEY) === 'true';
    if (hasFlowFlag) {
      const savedData = localStorage.getItem(this.config.localStorageKey);
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          // Patch form with saved data if current values are empty
          const currentTitle = this.contributionForm.get('title')?.value || '';
          const currentPermission = this.contributionForm.get('permission')?.value || '';
          
          let titleRestored = false;
          if (parsedData.title && !currentTitle) {
            this.contributionForm.patchValue({
              title: parsedData.title
            });
            // Mark as touched and update validity
            this.contributionForm.get('title')?.markAsTouched();
            this.contributionForm.get('title')?.updateValueAndValidity();
            titleRestored = true;
          }
          if (parsedData.permission && !currentPermission) {
            this.contributionForm.patchValue({
              permission: parsedData.permission
            });
            // Mark as touched and update validity
            this.contributionForm.get('permission')?.markAsTouched();
            this.contributionForm.get('permission')?.updateValueAndValidity();
          }
          if (parsedData.metadataOption !== undefined && !this.contributionForm.get('metadataOption')?.value) {
            this.contributionForm.patchValue({
              metadataOption: parsedData.metadataOption
            });
          }
          
          // Trigger title availability check if title was restored (for both datasets and models)
          if (titleRestored && (this.config.type === 'dataset' || this.config.type === 'model') && parsedData.title) {
            // Manually trigger the title availability check
            setTimeout(() => {
              this.checkTitleAvailability(parsedData.title).then((isAvailable) => {
                this.titleAvailabilityStatus = isAvailable ? 'available' : 'not-available';
                this.isCheckingTitle = false;
                this.cdr.detectChanges();
              }).catch((error) => {
                console.error('Error checking title availability after restore:', error);
                this.isCheckingTitle = false;
                this.titleAvailabilityStatus = null;
                this.cdr.detectChanges();
              });
            }, 100);
          }
          
          // Force change detection to update button states
          this.cdr.detectChanges();
        } catch (error) {
          console.error(`Error parsing saved ${this.config.type} data in ngAfterViewInit:`, error);
        }
      }
    }

    // Re-apply lock after restore (covers cases where itemId was added later)
    this.applyUpdateModeLock();
  }

  private applyUpdateModeLock(): void {
    const raw = localStorage.getItem(this.config.localStorageKey);
    if (!raw) {
      this.isUpdateMode = false;
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      const hasItemId = !!(parsed?.itemId || parsed?.id || parsed?.item_id);
      this.isUpdateMode = hasItemId;
      if (this.isUpdateMode) {
        this.isPermissionDropdownOpen = false;
        this.contributionForm.disable({ emitEvent: false });
      }
    } catch {
      this.isUpdateMode = false;
    }
  }

  onStepChange(stepIndex: number): void {
    // Save form data before navigating
    this.saveFormData();
    
    // Mark that user is navigating within the flow
    sessionStorage.setItem(this.FLOW_FLAG_KEY, 'true');
    
    // Navigate to the appropriate route based on step index
    if (this.config.routes && stepIndex >= 0 && stepIndex < this.config.routes.length) {
      const formData = {
        title: this.contributionForm.get('title')?.value,
        permission: this.contributionForm.get('permission')?.value,
        metadataOption: this.contributionForm.get('metadataOption')?.value
      };
      
      this.router.navigate([this.config.routes[stepIndex]], {
        state: { contributionData: formData }
      });
    }
  }

  onNext(): void {
    // Navigate to next step (metadata)
    if (this.canProceedToStep2() && this.config.routes && this.config.routes.length > 1) {
      this.router.navigate([this.config.routes[1]]);
    }
  }

  onPrevious(): void {
    // Save form data before navigating back
    this.saveFormData();
    
    // Mark that user is navigating within the flow
    sessionStorage.setItem(this.FLOW_FLAG_KEY, 'true');
    
    // Navigate to previous step (step 1)
    if (this.config.routes && this.config.routes.length > 0) {
      const formData = {
        title: this.contributionForm.get('title')?.value,
        permission: this.contributionForm.get('permission')?.value,
        metadataOption: this.contributionForm.get('metadataOption')?.value
      };
      
      this.router.navigate([this.config.routes[0]], {
        state: { contributionData: formData }
      });
    }
  }

  togglePermissionDropdown(): void {
    if (this.isUpdateMode) return;
    this.isPermissionDropdownOpen = !this.isPermissionDropdownOpen;
  }

  selectPermission(option: { value: 'Open' | 'Restricted'; label: string }): void {
    if (this.isUpdateMode) return;
    this.contributionForm.patchValue({ permission: option.value });
    this.isPermissionDropdownOpen = false;
    this.contributionForm.get('permission')?.markAsTouched();
  }

  getSelectedPermissionLabel(): string {
    const selectedValue = this.contributionForm.get('permission')?.value;
    if (!selectedValue) {
      return 'Select permission';
    }
    const selectedOption = this.permissionOptions.find(opt => opt.value === selectedValue);
    return selectedOption ? selectedOption.label : 'Select permission';
  }

  isPermissionSelected(value: string): boolean {
    return this.contributionForm.get('permission')?.value === value;
  }

  clearPermission(event: Event): void {
    if (this.isUpdateMode) return;
    event.stopPropagation();
    this.contributionForm.patchValue({ permission: '' });
    this.contributionForm.get('permission')?.markAsTouched();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const permissionWrapper = this.elementRef.nativeElement.querySelector('.custom-dropdown-wrapper');
    if (!permissionWrapper || !permissionWrapper.contains(event.target)) {
      this.isPermissionDropdownOpen = false;
    }
  }

  selectMetadataOption(option: 'enter' | 'upload'): void {
    if (this.isUpdateMode) return;
    this.contributionForm.patchValue({ metadataOption: option });
    
    // Save form data to localStorage (including metadataOption to track flow)
    this.saveFormData();
    
    // Mark that user is navigating within the flow
    sessionStorage.setItem(this.FLOW_FLAG_KEY, 'true');
    
    const formData = {
      title: this.contributionForm.get('title')?.value,
      permission: this.contributionForm.get('permission')?.value,
      metadataOption: option
    };
    
    if (option === 'enter') {
      // Navigate to metadata component with form data
      if (this.config.routes && this.config.routes.length > 1) {
        this.router.navigate([this.config.routes[1]], {
          state: { contributionData: formData }
        });
      }
    } else if (option === 'upload') {
      // Navigate to upload-metadata component
      if (this.config.routes && this.config.routes.length > 1) {
        const routeBase = this.config.routes[1].replace('/metadata', '');
        this.router.navigate([`${routeBase}/upload-metadata`], {
          state: { contributionData: formData }
        });
      }
    }
  }
  
  /**
   * Save form data to localStorage
   */
  private saveFormData(): void {
    const formData = {
      title: this.contributionForm.get('title')?.value || '',
      permission: this.contributionForm.get('permission')?.value || '',
      metadataOption: this.contributionForm.get('metadataOption')?.value || null
    };

    let existing: Record<string, unknown> = {};
    try {
      existing = JSON.parse(localStorage.getItem(this.config.localStorageKey) || '{}');
    } catch {
      /* ignore malformed storage */
    }
    localStorage.setItem(
      this.config.localStorageKey,
      JSON.stringify({ ...existing, ...formData }),
    );
  }
  
  /**
   * Check if a route is within the contribution flow
   */
  private isRouteInFlow(route: string): boolean {
    if (!route) return false;
    
    // Extract pathname from full URL if it's a full URL
    let pathname = route;
    try {
      if (route.startsWith('http://') || route.startsWith('https://')) {
        const url = new URL(route);
        pathname = url.pathname;
      }
    } catch (e) {
      // If URL parsing fails, use route as-is
    }
    
    // Check if route contains any of the contribution flow routes
    return this.config.routes.some(flowRoute => 
      pathname.includes(flowRoute) || 
      pathname.includes('/metadata') || 
      pathname.includes('/additionalInfo') || 
      pathname.includes('/uploads') ||
      pathname.includes('/upload-metadata') ||
      pathname.includes('/preview-metadata')
    );
  }

  isStep1Valid(): boolean {
    return this.contributionForm.get('title')?.valid === true && 
           this.contributionForm.get('permission')?.valid === true;
  }

  canEnableMetadataButtons(): boolean {
    // Check if all required fields are filled
    const fieldsValid = this.isStep1Valid();
    
    // For datasets and models, require title to be available (no validation error)
    if (this.config.type === 'dataset' || this.config.type === 'model') {
      return fieldsValid &&
             this.titleAvailabilityStatus === 'available' &&
             !this.isCheckingTitle;
    }
    
    return fieldsValid;
  }

  canProceedToStep2(): boolean {
    return this.isStep1Valid() && this.contributionForm.get('metadataOption')?.value !== null;
  }
}
